import { useCharacterProgressionStore } from '../store/characterProgressionSlice';
import { TALENT_TREE } from '../data/talent-tree.config';
import type { BuffModifier } from '../types/buff.types';
import type { EffectStep } from '../types/effect.types';
import type { TalentNodeDefinition, TalentNodeId } from '../types/talent.types';

/**
 * Smart layer around `useCharacterProgressionStore` for spending talent points in the
 * tree. The store stays neutral: it accepts any `nodeId` and decrements.
 * Validation (adjacency, budget, duplicate) lives here.
 */

/** True if the node exists and is spendable (budget + adjacency OK). */
export function canSpend(nodeId: TalentNodeId): boolean {
  const node = TALENT_TREE.nodes[nodeId];
  if (!node) {
    return false;
  }
  const store = useCharacterProgressionStore.getState();
  if (store.talentPoints <= 0) {
    return false;
  }
  if (store.pointsSpent.includes(nodeId)) {
    return false;
  }
  if (TALENT_TREE.roots.includes(nodeId)) {
    return true;
  }
  return node.connectsTo.some((neighbor) =>
    store.pointsSpent.includes(neighbor),
  );
}

/**
 * Spends a point on a node. Returns `true` if the spend happened,
 * `false` otherwise (unknown node, already taken, no point, or no
 * adjacency).
 */
export function spendPoint(nodeId: TalentNodeId): boolean {
  if (!canSpend(nodeId)) {
    return false;
  }
  useCharacterProgressionStore.getState().spendPoint(nodeId);
  return true;
}

/**
 * Full refund: returns all spent points to the available pool. The cost
 * in currency / materials is the caller's responsibility (calibration parked).
 */
export function respec(): void {
  useCharacterProgressionStore.getState().refundAllPoints();
}

/** Aggregates the `BuffModifier[]` carried by all currently taken nodes. */
export function getActiveModifiers(): BuffModifier[] {
  const spent = useCharacterProgressionStore.getState().pointsSpent;
  const out: BuffModifier[] = [];
  for (const id of spent) {
    const node = TALENT_TREE.nodes[id];
    if (node?.modifiers) {
      out.push(...node.modifiers);
    }
  }
  return out;
}

/** Aggregates the `EffectStep[]` carried by the taken notable nodes. */
export function getActiveEffects(): EffectStep[] {
  const spent = useCharacterProgressionStore.getState().pointsSpent;
  const out: EffectStep[] = [];
  for (const id of spent) {
    const node = TALENT_TREE.nodes[id];
    if (node?.effects) {
      out.push(...node.effects);
    }
  }
  return out;
}

/** Returns all currently taken nodes. */
export function getSpentNodes(): TalentNodeDefinition[] {
  const spent = useCharacterProgressionStore.getState().pointsSpent;
  return spent
    .map((id) => TALENT_TREE.nodes[id])
    .filter((n): n is TalentNodeDefinition => !!n);
}
